import { Box, colors } from "@biblioteksentralen/react";
import { capitalizeFirstLetter, SIMPLE_ISO_DATE_FORMAT, WEEKDAY_LABELS } from "@libry-content/common";
import format from "date-fns/format";
import nb from "date-fns/locale/nb/index.js";
import "react-day-picker/dist/style.css";
import { DateAfter, DateBefore, DayPicker, DayPickerProps, MonthCaptionProps } from "react-day-picker";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { localizedFormat } from "../../utils/date";
import { sift } from "radash";

const LABELS = { labelNext: () => "neste måned", labelPrevious: () => "forrige måned" };

const styles = `
  .rdp-root {
    --rdp-background-color: #f2f2f2;
  }

  .rdp-disabled {
    opacity: 0.5;
  }

  .rdp-today {
    font-weight: 600;
    color: inherit; 
  }

  .rdp-selected {
    background-color: ${colors.accentBlueMedium};
    color: white;
    border-radius: 50%;
  }

  .rdp-selected .rdp-day_button {
      border: none;
  }

  .rdp-chevron {
    fill: ${colors.accentBlueMedium};
  }
`;

// DayPicker only shows two letters of weekdays so we use a custom head row component
const Weekdays = ({ hideWeekNumber }: { hideWeekNumber?: boolean }) => {
  return (
    <thead>
      <tr className="rdp-weekdays">
        {!hideWeekNumber && <th className="rdp-weekday"></th>}
        {Object.values(WEEKDAY_LABELS.nb).map((day) => (
          <th className="rdp-weekday" key={day}>
            {day.slice(0, 3).toLowerCase()}
          </th>
        ))}
      </tr>
    </thead>
  );
};

const MonthCaption = ({ calendarMonth }: MonthCaptionProps) => {
  const { lang } = useTranslation();
  const monthName = localizedFormat(calendarMonth.date.toISOString(), "LLLL", lang);

  return (
    <Box className="rdp-month_caption" marginLeft="1rem">
      <span className="rdp-caption_label">{capitalizeFirstLetter(monthName)}</span>
    </Box>
  );
};

type Props = DayPickerProps & {
  onChange: (date: string | undefined) => void;
  value: string | undefined;
  dateRange?: { start?: Date; end?: Date };
  hideWeekNumber?: boolean;
};

export const CalendarInput = (props: Props) => {
  const { lang } = useTranslation();
  const selectedDate = props.value ? new Date(props.value) : undefined;

  const dateBeforeMatcher: DateBefore | undefined = props.dateRange?.start && { before: props.dateRange.start };
  const dateAfterMatcher: DateAfter | undefined = props.dateRange?.end && { after: props.dateRange.end };
  const disabledMatchers = sift([dateBeforeMatcher, dateAfterMatcher]);

  return (
    <Box css={styles}>
      <DayPicker
        weekStartsOn={1}
        locale={lang}
        mode="single"
        selected={selectedDate}
        showWeekNumber={!props.hideWeekNumber}
        disabled={disabledMatchers}
        components={{ Weekdays: () => <Weekdays hideWeekNumber={props.hideWeekNumber} />, MonthCaption }}
        startMonth={props.dateRange?.start}
        endMonth={props.dateRange?.end}
        labels={LABELS}
        onDayClick={(date) => {
          const newDate = format(date, SIMPLE_ISO_DATE_FORMAT, { locale: nb });
          if (newDate === props.value) props.onChange(undefined);
          else props.onChange(newDate);
        }}
      />
    </Box>
  );
};
